import { ActionContext, MutationTree, ActionTree, GetterTree } from 'vuex'
import { DeadlineListItem, Deadline, Customer, Country, ServiceLevel, DeadlineFilter } from '../webApi/DataModels'
import webApi from '../webApi'
import { filter, each, extend, find, isEmpty, intersection, orderBy } from 'lodash'
import moment from 'moment'

interface IDeadlineListFilter {
  City: string;
  Customer_IDs: number[];
  Country_IDs: number[];
  Service_Level_IDs: number[];
  From_Zip_Code: string;
  To_Zip_Code: string;
  Valid_From: string;
  Valid_To: string;
}

function resolveValues (id: number, values: any[], idFieldName: string, valueFieldName: string, resolvedLabel: string): String {
  var resolvedValues = []
  var filterObject = {}
  filterObject[idFieldName] = id
  var value = find(values, filterObject)
  if (value && value[valueFieldName]) {
    resolvedValues.push(value[valueFieldName])
  }
  if (resolvedValues.length > 3) {
    return resolvedValues.length + ' ' + resolvedLabel
  } else if (resolvedValues.length === 0) {
    return ''
  } else {
    return resolvedValues.join(', ')
  }
}

class DeadlineState {
  public deadlineList: DeadlineListItem[] = []
  public filteredDeadlineList: DeadlineListItem[] = []
  public loadedDeadline: Deadline = null
  public deadlineResult: any = null
  public currentEditDeadline: Deadline = null
  public deadlineFilter: DeadlineFilter = null
}

const mutations = <MutationTree<DeadlineState>> {
  // sets the deadline list and resolves the country, customer and service level names
  setDeadlineList (state: DeadlineState, { list, customers, countries, serviceLevels }: { list: DeadlineListItem[], customers: Customer[], countries: Country[], serviceLevels: ServiceLevel[] }) {
    each(list, (deadlineItem: Deadline, index: number) => {
      extend(deadlineItem, {
        Country_Name: resolveValues(deadlineItem.Country_ID, countries, 'Country_ID', 'Country_Name', 'Countries')
      })

      extend(deadlineItem, {
        Customer_Name: resolveValues(deadlineItem.Customer_ID, customers, 'Customer_ID', 'Customer_Name', 'Customers')
      })

      extend(deadlineItem, {
        Service_Level_Name: resolveValues(deadlineItem.Service_Level_ID, serviceLevels, 'Service_Level_ID', 'Service_Level_Name', 'ServiceLevels')
      })
      extend(deadlineItem, {
        User_Name: deadlineItem.User.Full_Name
      })
    })

    state.deadlineList = list
    state.filteredDeadlineList = list
  },
  setCurrentEditDeadline (state: DeadlineState, deadline: Deadline): void {
    state.currentEditDeadline = deadline
  },
  setLoadedDeadline (state: DeadlineState, deadline: Deadline): void {
    state.loadedDeadline = deadline
  },
  filteredDeadlineList (state:DeadlineState, { sortFields, sortDirections }) {
    state.filteredDeadlineList = orderBy(state.filteredDeadlineList, sortFields, sortDirections)
    state.deadlineList = orderBy(state.deadlineList, sortFields, sortDirections)
  },
  setDeadlineFilter (state: DeadlineState, deadlineFilter: DeadlineFilter) {
    state.deadlineFilter = deadlineFilter
  },
  reset (state:DeadlineState) {
    state.deadlineList = []
    state.filteredDeadlineList = []
    state.loadedDeadline = null
    state.deadlineResult = null
    state.currentEditDeadline = null
    state.deadlineFilter = null
  },
  resetDeadline (state: DeadlineState): void {
    state.loadedDeadline = null
  }
}

const actions = <ActionTree<DeadlineState, any>>{
  reset (store: ActionContext<DeadlineState, any>):void {
    store.commit('reset')
  },
  loadDeadlineFilter (store: ActionContext<DeadlineState, any>, deadlineFilter: DeadlineFilter) {
    store.commit('setDeadlineFilter', deadlineFilter)
  },
  // loads the deadline list
  loadDeadlineList (store: ActionContext<DeadlineState, any>, deadlineListFilter: IDeadlineListFilter): Promise<any> {
    return webApi.deadline.get_Deadline_List(deadlineListFilter).then(
      (response) => {
        store.commit('setDeadlineList', {
          list: response.data,
          customers: store.rootState.customer.customers,
          countries: store.rootState.country.countries,
          serviceLevels: store.rootState.serviceLevel.serviceLevels
        })
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // loads a deadline
  loadDeadline (store: ActionContext<DeadlineState, any>, deadlineID: number): Promise<any> {
    store.commit('resetDeadline')
    return webApi.deadline.get_Deadline_By_ID(deadlineID).then(
      (response: any) => {
        var result: Deadline = response.data as Deadline
        store.commit('setLoadedDeadline', result)
        return Promise.resolve('success')
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  // saves the deadline to the database
  saveDeadline (store: ActionContext<DeadlineState, any>, deadline: Deadline): Promise<any> {
    return webApi.deadline.save_Deadline(deadline).then(
      (response) => {
        var filter = {
          customers: [deadline.Customer_ID],
          countries: [deadline.Country_ID],
          serviceLevels: [deadline.Service_Level_ID],
          city: deadline.City,
          fromPostalCode: deadline.From_Postal_Code,
          toPostalCode: deadline.To_Postal_Code,
          validFrom: moment(deadline.Valid_From).format(),
          validTo: deadline.Valid_To
        }
        store.commit('setDeadlineFilter', filter)
        return Promise.resolve(response)
      }).catch((response) => {
      window.bus.$emit('openErrorModal', response.data.ExceptionMessage)
      return Promise.reject(response)
    })
  },
  sortList ({ commit }, { sortFields, sortDirections }): void {
    commit('filteredDeadlineList', { sortFields, sortDirections })
  }
}

const getters = <GetterTree<DeadlineState, any>>{
  deadlineList: (state): DeadlineListItem[] => {
    return state.filteredDeadlineList
  },
  loadedDeadline: (state): Deadline => {
    return state.loadedDeadline
  },
  deadlineResult: (state): any => {
    return state.deadlineResult
  },
  deadlineFilter: (state: DeadlineState): DeadlineFilter => {
    return state.deadlineFilter
  }
}

export default ({
  namespaced: true,
  state: new DeadlineState(),
  mutations,
  actions,
  getters
})
